import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { participantsAPI } from '../services/api';
import Loading from '../components/Loading';
import StoryGenerator from '../components/StoryGenerator';
import StitchHeader from '../components/StitchHeader';
import '../styles/TestDetail.css';

function TestDetail() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [test, setTest] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStoryGenerator, setShowStoryGenerator] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost/backend';

  const fetchTestDetail = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const testResponse = await fetch(`${apiUrl}/api/tests/detail.php?id=${testId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const testData = await testResponse.json();

      if (testData.success) {
        setTest(testData.test);
      }

      const participantsResponse = await participantsAPI.getResults(testId);
      if (participantsResponse.data.success) {
        setParticipants(participantsResponse.data.participants);
        setStats(participantsResponse.data.stats);
      }
    } catch (requestError) {
      console.error('Error:', requestError);
      setError(requestError.error || requestError.message || 'خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, testId]);

  useEffect(() => {
    fetchTestDetail();
  }, [fetchTestDetail]);

  const handleViewDetail = async (participantId) => {
    setShowDetailModal(true);

    try {
      const response = await participantsAPI.getDetail(participantId);
      if (response.data.success) {
        setParticipantDetail(response.data);
      }
    } catch (requestError) {
      alert(requestError.error || 'خطا در بارگذاری جزئیات');
      setShowDetailModal(false);
    }
  };

  const handleCopyLink = async () => {
    if (!test?.test_uuid) {
      alert('لطفاً چند لحظه صبر کنید.');
      return;
    }

    const link = `${window.location.origin}/t/${test.test_uuid}`;
    try {
      await navigator.clipboard.writeText(link);
      alert('لینک آزمون کپی شد.');
    } catch (copyError) {
      console.error('Copy link error:', copyError);
      alert(link);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#006e4b';
    if (score >= 60) return '#3525cd';
    if (score >= 40) return '#b7791f';
    return '#ba1a1a';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'عالی';
    if (score >= 60) return 'خوب';
    if (score >= 40) return 'متوسط';
    return 'ضعیف';
  };

  if (loading) return <Loading message="در حال بارگذاری..." />;

  if (error) {
    return (
      <div className="test-detail-error">
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')} type="button">بازگشت به داشبورد</button>
      </div>
    );
  }

  const testName = test?.title || test?.name || test?.category_name || 'آزمون شما';

  return (
    <div className="test-detail-container">
      <StitchHeader />
      <main className="test-detail-main">
        <section className="test-detail-page-heading">
          <div>
            <h1>جزئیات شرکت‌کنندگان</h1>
            <p><span />آزمون: {testName}</p>
          </div>
          <div className="test-detail-actions">
            <button onClick={handleCopyLink} className="detail-share-action" type="button">
              <span className="material-symbols-outlined">share</span>
              اشتراک‌گذاری
            </button>
            <button onClick={() => setShowStoryGenerator(true)} className="detail-story-action" type="button">
              <span className="material-symbols-outlined">auto_awesome</span>
              ساخت استوری
            </button>
          </div>
        </section>

        {stats && (
          <section className="detail-stat-grid">
            <article className="detail-stat-card detail-stat-primary"><div><span>کل شرکت‌کنندگان</span><i className="material-symbols-outlined">groups</i></div><strong>{stats.total.toLocaleString('fa-IR')}</strong></article>
            <article className="detail-stat-card detail-stat-tertiary"><div><span>میانگین امتیاز</span><i className="material-symbols-outlined">star</i></div><strong>{stats.average.toLocaleString('fa-IR')}٪</strong></article>
            <article className="detail-stat-card detail-stat-secondary"><div><span>کمترین امتیاز</span><i className="material-symbols-outlined">trending_down</i></div><strong>{stats.lowest.toLocaleString('fa-IR')}٪</strong></article>
            <article className="detail-stat-card detail-stat-primary"><div><span>بیشترین امتیاز</span><i className="material-symbols-outlined">trophy</i></div><strong>{stats.highest.toLocaleString('fa-IR')}٪</strong></article>
          </section>
        )}

        <section className="participants-section">
          <div className="detail-list-heading">
            <div><h2>فهرست شرکت‌کنندگان</h2><p>{participants.length.toLocaleString('fa-IR')} پاسخ ثبت شده</p></div>
          </div>

          {participants.length === 0 ? (
            <div className="no-participants">
              <span className="material-symbols-outlined">group_add</span>
              <h3>هنوز پاسخی ثبت نشده است</h3>
              <p>لینک آزمون را برای دوستات بفرست تا اولین پاسخ‌ها را ببینی.</p>
              <button onClick={handleCopyLink} type="button"><span className="material-symbols-outlined">content_copy</span>کپی لینک آزمون</button>
            </div>
          ) : (
            <div className="participant-result-tiles">
              {participants.map((participant, index) => (
                <article key={participant.id} className="participant-result-tile">
                  <div className="participant-tile-header">
                    <div className="participant-tile-person"><span className={`rank-badge rank-${index + 1}`}>{index + 1}</span><span className="participant-initial">{participant.name?.charAt(0) || '؟'}</span><div><strong>{participant.name}</strong><small><span className="material-symbols-outlined">calendar_today</span>{new Date(participant.completed_at).toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' })}</small></div></div>
                    <span className="participant-tile-score" style={{ color: getScoreColor(participant.score) }}><b>{participant.score.toLocaleString('fa-IR')}٪</b>{getScoreLabel(participant.score)}</span>
                  </div>
                  <div className="participant-tile-progress"><div><span>پیشرفت پاسخ‌ها</span><strong>{participant.correct_count} از {participant.total_questions} پاسخ درست</strong></div><div className="participant-progress"><span style={{ width: `${participant.score}%`, background: getScoreColor(participant.score) }} /></div></div>
                  <button className="participant-tile-detail" onClick={() => handleViewDetail(participant.id)} type="button"><span>مشاهده پاسخ‌ها</span><span className="material-symbols-outlined">chevron_left</span></button>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {showDetailModal && participantDetail && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header"><h2>جزئیات پاسخ‌ها</h2><button onClick={() => setShowDetailModal(false)} className="btn-close-modal" type="button">×</button></div>
            <div className="modal-body">
              <div className="participant-info-card"><div className="participant-avatar">{participantDetail.participant.name.charAt(0)}</div><div className="participant-info"><h3>{participantDetail.participant.name}</h3><p>امتیاز: {participantDetail.participant.score}٪</p><p>{participantDetail.participant.correct_count} از {participantDetail.participant.total_questions} سؤال صحیح</p></div></div>
              <div className="answers-list">
                {participantDetail.answers.map((answer, index) => (
                  <article key={answer.question_id} className={`answer-item ${answer.is_correct ? 'correct' : 'incorrect'}`}>
                    <div className="answer-header"><span className="question-number">سؤال {index + 1}</span><span className={`answer-badge ${answer.is_correct ? 'correct' : 'incorrect'}`}>{answer.is_correct ? 'پاسخ صحیح' : 'پاسخ نادرست'}</span></div>
                    <p className="question-text">{answer.question}</p>
                    <div className="answer-comparison"><div className="answer-box participant-answer"><label>پاسخ شرکت‌کننده</label><span>{answer.participant_answer}</span></div>{!answer.is_correct && <div className="answer-box correct-answer"><label>پاسخ صحیح</label><span>{answer.correct_answer}</span></div>}</div>
                  </article>
                ))}
              </div>
            </div>
            <div className="modal-footer"><button onClick={() => setShowDetailModal(false)} type="button">بستن</button></div>
          </div>
        </div>
      )}

      {showStoryGenerator && test && <StoryGenerator testUuid={test.test_uuid} creatorName={user?.name} testType={test.test_type} questionsCount={test.questions_count || 10} onClose={() => setShowStoryGenerator(false)} />}
    </div>
  );
}

export default TestDetail;
